import React, { Component } from "react";
import { Paper, Box, Button, IconButton } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import makeStyles from "@mui/styles/makeStyles";
import PropTypes from "prop-types";
import Calendar from "../Calendar";
import moment from "moment";
import {
  Add,
  KeyboardArrowRight,
  KeyboardArrowLeft
} from "@mui/icons-material";
import CalendarSelector from "./CalendarSelector";
import clsx from "clsx";

const condensedButtonSize = 32;
const styles = theme => ({
  "@global": {
    ".control-panel-calendar": {
      position: "sticky",
      backgroundColor: theme.palette.body,
      right: 0,
      top: theme.sizes.topNavBarHeight,
      zIndex: 1,
      overflowX: "hidden",
      height: `calc(100vh - ${theme.sizes.topNavBarHeight}px)`,
      [theme.breakpoints.down("sm")]: {
        position: "absolute",
        top: 0,
        height: "100%"
      }
    }
  },
  panelExpanded: {
    width: theme.sizes.calendarControlPanelWidth,
    minWidth: theme.sizes.calendarControlPanelWidth,
    transition: theme.transitions.create("min-width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen
    })
  },
  panelCollapsed: {
    transition: theme.transitions.create("min-width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    overflow: "hidden",
    width: theme.sizes.sideNavBarWidthCondensed,
    minWidth: theme.sizes.sideNavBarWidthCondensed,
    [theme.breakpoints.down("sm")]: {
      width: theme.sizes.calendarControlPanelWidthCodensedMobile,
      minWidth: theme.sizes.calendarControlPanelWidthCodensedMobile
    }
  },
  expandButton: {
    position: "relative",
    top: `calc(50vh - ${condensedButtonSize}px - ${theme.sizes.topNavBarHeight}px)`,
    left: (theme.sizes.sideNavBarWidthCondensed - condensedButtonSize) / 2,
    maxWidth: condensedButtonSize,
    height: condensedButtonSize,
    [theme.breakpoints.down("sm")]: {
      left:
        (theme.sizes.calendarControlPanelWidthCodensedMobile -
          condensedButtonSize) /
        2
    }
  },
  controlsContainerExpanded: {
    transform: `translateX(-${theme.sizes.sideNavBarWidthCondensed}px)`,
    transition: theme.transitions.create("transform", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen
    }),
    [theme.breakpoints.down("sm")]: {
      transform: `translateX(-${theme.sizes.calendarControlPanelWidthCodensedMobile}px)`
    }
  }
});

const useStyles = makeStyles(theme => ({
  miniButton: {
    padding: 0,
    minWidth: 0,
    width: condensedButtonSize,
    height: condensedButtonSize,
    position: "relative",
    right:
      theme.spacing(3) +
      condensedButtonSize +
      (theme.sizes.sideNavBarWidthCondensed - condensedButtonSize) / 2,
    [theme.breakpoints.down("sm")]: {
      right:
        theme.spacing(3) +
        condensedButtonSize +
        (theme.sizes.calendarControlPanelWidthCodensedMobile -
          condensedButtonSize) /
          2
    }
  },
  miniButtonIcon: {
    margin: 0
  }
}));

const header = {
  left: "title",
  right: "prev, next"
};

const CreateEventButton = ({ onCreateEventClick, panelExpanded }) => {
  const classes = useStyles();
  return (
    <Button
      startIcon={<Add />}
      variant="contained"
      color="primary"
      onClick={onCreateEventClick}
      classes={{
        root: clsx({
          [classes.miniButton]: !panelExpanded
        }),
        startIcon: clsx({
          [classes.miniButtonIcon]: !panelExpanded
        })
      }}
    >
      {panelExpanded ? "Create Event" : ""}
    </Button>
  );
};

/* const CreateNewCalendarButton = ({ onCreateCalendarClick }) => {
  return (
    <Box mt={1.5}>
      <Button
        color="primary"
        startIcon={<Add />}
        onClick={onCreateCalendarClick}
      >
        Create New Calendar
      </Button>
    </Box>
  );
}; */

class CalendarControlPanel extends Component {
  constructor(props) {
    super(props);
    this.state = {
      panelExpanded: !props.collapsible
    };
    this.panelRef = React.createRef();
  }

  componentDidMount() {
    window.addEventListener("scroll", this.handleScroll);
  }

  /**
   * Expands the collapsed side nav if the user clicked on the side nav outside of a menu item
   * or if the user clicked on an expandable menu item
   */
  expandControlPanel = () => {
    this.setState({ panelExpanded: !this.state.panelExpanded });
  };

  /**
   * Gets the column header for the calendar view.
   */
  getColumnHeader = date => {
    const weekday = moment(date).format("dd")[0];
    return `<span class="timeGrid-col-header-day">${weekday}</span>`;
  };

  render() {
    const {
      classes,
      theme,
      events,
      calendarLists,
      userPermissions,
      handleDateClicked,
      onExportModalOpenChange,
      onImportModalOpenChange,
      onCreateEventModalOpenChange,
      onEditEventModalOpenChange
      // onCreateCalendarModalOpenChange
    } = this.props;
    const { panelExpanded } = this.state;
    return (
      <Paper
        data-qa="control-panel-calendar"
        className={clsx("control-panel-calendar", {
          [classes.panelExpanded]: panelExpanded,
          [classes.panelCollapsed]: !panelExpanded
        })}
      >
        <Box
          display="flex"
          flexDirection="row"
          position="relative"
          className={clsx({
            [classes.controlsContainerExpanded]: panelExpanded
          })}
        >
          <Box
            minWidth={{
              xs: theme.sizes.calendarControlPanelWidthCodensedMobile,
              sm: theme.sizes.sideNavBarWidthCondensed
            }}
            position="relative"
            onClick={() => this.expandControlPanel()}
          >
            <IconButton
              onClick={() => this.expandControlPanel()}
              className={classes.expandButton}
              size="large"
            >
              <KeyboardArrowLeft color="action" />
            </IconButton>
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            minWidth={theme.sizes.calendarControlPanelWidth}
            px={3}
            py={4}
          >
            {!(userPermissions.silver || userPermissions.validate) && (
              <Box display="flex" mb={5} justifyContent="space-between">
                <CreateEventButton
                  onCreateEventClick={() => onCreateEventModalOpenChange(true)}
                  panelExpanded={panelExpanded}
                />
                <IconButton
                  onClick={() => this.expandControlPanel()}
                  size="large"
                >
                  <KeyboardArrowRight color="action" />
                </IconButton>
              </Box>
            )}

            <Calendar
              events={events}
              header={header}
              columnHeaderHtml={this.getColumnHeader}
              eventLimit={1}
              handleDateClicked={handleDateClicked}
              handleEventClicked={() => onEditEventModalOpenChange(true)}
            />
            {calendarLists && (
              <CalendarSelector
                userPermissions={userPermissions}
                calendarLists={calendarLists}
                onExportModalOpenChange={onExportModalOpenChange}
                onImportModalOpenChange={onImportModalOpenChange}
              />
            )}
            {/*
              * TODO: Implement functionality for create new calendar button
             <CreateNewCalendarButton
              onCreateCalendarClick={() =>
                onCreateCalendarModalOpenChange(true)
              }
            />*/}
          </Box>
        </Box>
      </Paper>
    );
  }
}

export default withStyles(styles, { withTheme: true })(CalendarControlPanel);

CalendarControlPanel.propTypes = {
  classes: PropTypes.object,
  theme: PropTypes.object,
  events: PropTypes.array,
  calendarLists: PropTypes.object,
  userPermissions: PropTypes.any,
  handleEventClicked: PropTypes.func,
  handleDateClicked: PropTypes.func
};
